import React from "react"
import CircularProgress from "material-ui/CircularProgress"
import UserStore from "../../../stores/UserStore"
import UserDetailsSummary from "./UserDetailsSummary"
import UserDetailsData from "./UserDetailsData"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import autobind from "class-autobind"
import * as userActions from "../../../actions/UserActions"
import * as permissionHelper from "../../../helpers/permissionHelper"
import { IUserDocument, ELoggedInUserType, IHolidayInstanceForPunch } from "../../../models"
import strings from "../../../localization/strings"
import * as userHelper from "../../../helpers/userHelper"
import * as jobHelper from "../../../helpers/jobHelper"
import AppStateStore from "../../../stores/AppStateStore"
import AccountStore from "../../../stores/AccountStore"
import * as timeHelper from "../../../helpers/timeHelper"

interface IProps {
	location: { query: any }
	params: { userId: string; subpage: string }
}
interface IState {
	showProgress: boolean
	currentUser: IUserDocument
	userStatsTodayHours: number
	userStatsThisWeekHours: number
	readonly: boolean
	readonlyForHierarchy: boolean
	editDevicePermission: boolean
	addDevicePermission: boolean
	deleteDevicePermission: boolean
	timecardReadOnly: boolean
	loggedInUserType: ELoggedInUserType
	supervisorReadonlyForSelf: boolean
	holidays: IHolidayInstanceForPunch[]
}

export default class UserDetails extends React.Component<IProps, IState> {
	constructor(props) {
		super(props)
		this.state = {
			showProgress: true,
			currentUser: UserStore.getCurrentUserDetail() || null,
			userStatsTodayHours: UserStore.getUserStatsTodayHours(),
			userStatsThisWeekHours: UserStore.getUserStatsThisWeekHours(),
			readonly: !permissionHelper.hasPermission(AuthenticationStore.Permissions.Employees.EditEmployees),
			readonlyForHierarchy: !permissionHelper.hasPermission(AuthenticationStore.Permissions.Account.EditHierarchy),
			editDevicePermission: permissionHelper.hasPermission(AuthenticationStore.Permissions.Devices.EditUserDevices),
			addDevicePermission: permissionHelper.hasPermission(AuthenticationStore.Permissions.Devices.AddUserDevices),
			deleteDevicePermission: permissionHelper.hasPermission(AuthenticationStore.Permissions.Devices.DeleteUserDevices),
			timecardReadOnly: !permissionHelper.hasPermission(AuthenticationStore.Permissions.Timecards.EditTimecards),
			loggedInUserType: permissionHelper.getLoggedInUserType(),
			supervisorReadonlyForSelf: UserStore.getCurrentUserDetail()
				? UserStore.getCurrentUserDetail().Id === AuthenticationStore.getLoggedInUser().Id && !permissionHelper.hasPermission(AuthenticationStore.Permissions.Employees.AllowSelfEmpEdit)
				: false,
			holidays: []
		}
		autobind(this)
	}

	componentDidMount() {
		UserStore.on("change", this.changeEvent)
		UserStore.on("change_reload_self_service_leave_dashboard", this.reloadSelfServiceChangeEvent)
		AuthenticationStore.on("change", this.authChangeEvent)
		this.getUser()
	}

	componentWillReceiveProps(nextProps: IProps) {
		this.props.params.userId !== nextProps.params.userId &&
			this.getUser()
	}

	componentWillUnmount() {
		UserStore.clearCurrentUserDetail()
		UserStore.removeListener("change", this.changeEvent)
		UserStore.removeListener("change_reload_self_service_leave_dashboard", this.reloadSelfServiceChangeEvent)
		AuthenticationStore.removeListener("change", this.authChangeEvent)
	}

	getUser() {
		setTimeout(() => {
			userActions.loadUserDetails(this.props.params.userId).then(res => document.title = `${strings.Employee} #${res.data.ExternalId} | ${strings.Findd_Grandd_Central}`)
			userActions.getAllHolidays(this.props.params.userId)
		}, 0)
	}

	reloadSelfServiceChangeEvent() {
		setTimeout(() => userActions.loadUserDetails(this.props.params.userId, false), 0)
	}

	authChangeEvent() {
		this.setState({
			readonly: !permissionHelper.hasPermission(AuthenticationStore.Permissions.Employees.EditEmployees),
			readonlyForHierarchy: !permissionHelper.hasPermission(AuthenticationStore.Permissions.Account.EditHierarchy),
			editDevicePermission: permissionHelper.hasPermission(AuthenticationStore.Permissions.Devices.EditUserDevices),
			addDevicePermission: permissionHelper.hasPermission(AuthenticationStore.Permissions.Devices.AddUserDevices),
			deleteDevicePermission: permissionHelper.hasPermission(AuthenticationStore.Permissions.Devices.DeleteUserDevices),
			timecardReadOnly: !permissionHelper.hasPermission(AuthenticationStore.Permissions.Timecards.EditTimecards),
			loggedInUserType: permissionHelper.getLoggedInUserType()
		})
	}

	changeEvent() {
		const currentUser = UserStore.getCurrentUserDetail() as IUserDocument
		let accessPage = true
		if (currentUser && this.state.loggedInUserType !== ELoggedInUserType.Employee && AccountStore.getJobsEnabled()) {
			accessPage = jobHelper.getSupervisorAccessJobPermission(currentUser.DefaultJobId)
		}
		accessPage ?
			this.setState({
				showProgress: UserStore.isFetchingUserDetail(),
				currentUser: currentUser,
				userStatsTodayHours: UserStore.getUserStatsTodayHours(),
				userStatsThisWeekHours: UserStore.getUserStatsThisWeekHours(),
				supervisorReadonlyForSelf: currentUser ?
					currentUser.Id === AuthenticationStore.getLoggedInUser().Id && !permissionHelper.hasPermission(AuthenticationStore.Permissions.Employees.AllowSelfEmpEdit)
					: false,
				holidays: UserStore.getHolidays()
			}) :
			AppStateStore.setNoAccessPage()
	}

	onUpdateProfilePic(id, faceSpace) {
		userHelper.checkLoggedInUserPermission(this.state.currentUser) &&
			userActions.updateProfilePic(id, faceSpace)
	}

	onDeleteProfilePic() {
		userHelper.checkLoggedInUserPermission(this.state.currentUser) &&
			userActions.removeProfilePic(this.state.currentUser.Id)
	}

	onUploadProfilePic(imageData) {
		userHelper.checkLoggedInUserPermission(this.state.currentUser) &&
			userActions.uploadProfilePic(this.state.currentUser.Id, imageData)
	}

	render() {
		return (
			<div className="page-container UserDetails">
				{this.state.showProgress ?
					<div className="spinner" style={{ display: "flex", flex: "1 1 100%", placeContent: "center", height: "75vh", paddingTop: "12em" }}>
						<CircularProgress />
					</div> :
					<div className="user-details">
						<UserDetailsSummary
							user={this.state.currentUser}
							userStatsTodayHours={this.state.userStatsTodayHours}
							userStatsThisWeekHours={this.state.userStatsThisWeekHours}
							onDeleteProfilePic={this.onDeleteProfilePic}
							onUploadProfilePic={this.onUploadProfilePic}
							readonly={this.state.readonly || this.state.supervisorReadonlyForSelf || this.state.currentUser.Deleted}
							loggedInUserType={this.state.loggedInUserType}
							supervisorReadonlyForSelf={this.state.supervisorReadonlyForSelf}
						/>
						<UserDetailsData
							user={this.state.currentUser}
							userId={this.props.params.userId}
							onUpdateProfilePic={this.onUpdateProfilePic}
							supervisorReadonlyForSelf={this.state.supervisorReadonlyForSelf}
							readonly={this.state.readonly || this.state.supervisorReadonlyForSelf || this.state.currentUser.Deleted}
							readonlyForHierarchy={this.state.readonlyForHierarchy || this.state.supervisorReadonlyForSelf || this.state.currentUser.Deleted}
							editDevicePermission={this.state.editDevicePermission && !this.state.supervisorReadonlyForSelf && !this.state.currentUser.Deleted}
							addDevicePermission={this.state.addDevicePermission && !this.state.supervisorReadonlyForSelf && !this.state.currentUser.Deleted}
							deleteDevicePermission={this.state.deleteDevicePermission && !this.state.supervisorReadonlyForSelf && !this.state.currentUser.Deleted}
							timecardReadOnly={this.state.timecardReadOnly || this.state.supervisorReadonlyForSelf || this.state.currentUser.Deleted}
							timecardApprovals={!this.state.supervisorReadonlyForSelf}
							tabActive={this.props.params.subpage}
							loggedInUserType={this.state.loggedInUserType}
							readonlyForIndependentPermission={this.state.supervisorReadonlyForSelf || this.state.currentUser.Deleted}
							readonlySchedule={
								!permissionHelper.hasPermission(AuthenticationStore.Permissions.Schedule.EditUserSchedule) || this.state.supervisorReadonlyForSelf || this.state.currentUser.Deleted
							}
							holidays={this.state.holidays}
							timecardReferenceDate={this.props.location.query.referenceDate ?
								timeHelper.isUTC(this.props.location.query.referenceDate) ?
									timeHelper.keepConvertUTCToLocal(this.props.location.query.referenceDate) :
									this.props.location.query.referenceDate :
								null
							}
						/>
					</div>
				}
			</div>
		)
	}
}
